import { faBoxOpen, faObjectGroup, faRecycle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React from 'react';
import { POWDER_QUALITY_BADGE_DATA } from 'src/components/BatchPowderQualityStatusIcon';
import { BLEND_ATTRIBUTE, getPowderQualityLabel } from 'src/utils/blendAttributes';
import { pluralWord } from 'src/utils/stringUtils';
import { getShortUuid } from 'src/utils/url';

const MaterialLotValues = ({ lotUris, lotsByUri, uniqueLotUris }) => (
  <>
    {lotUris.map(lotUri => {
      const lot = lotsByUri[lotUri];

      return (
        <div key={lotUri} className='blend-value blend-value--item'>
          <FontAwesomeIcon icon={faBoxOpen} />
          <div className='blend-value__body'>
            {/* Lot name is primary, material secondary - as in MaterialLotCardDetails */}
            <div className='blend-value__name'>{lot?.name || getShortUuid(lotUri)}</div>
            {lot?.material_name && (
              <div className='blend-value__material'>
                <FontAwesomeIcon icon={faObjectGroup} />
                <span>{lot.material_name}</span>
              </div>
            )}
            {uniqueLotUris.has(lotUri) && <span className='blend-value__tag'>Not in source</span>}
          </div>
        </div>
      );
    })}
  </>
);

const MaterialTypeValues = ({ materials }) => (
  <>
    {materials.map(material => (
      <div key={material.uri || material.name} className='blend-value blend-value--item'>
        <FontAwesomeIcon icon={faObjectGroup} />
        <div className='blend-value__body'>
          <div className='blend-value__name'>{material.name}</div>
        </div>
      </div>
    ))}
  </>
);

const UsageCyclesValue = ({ cycles }) => (
  <div className='blend-value'>
    <div className='blend-value__number'>
      <FontAwesomeIcon icon={faRecycle} />
      <span>{cycles}</span>
    </div>
    <span className='blend-value__unit'>{pluralWord('cycle', cycles)}</span>
  </div>
);

const PowderQualityValue = ({ status }) => {
  const badge = POWDER_QUALITY_BADGE_DATA[status];

  if (!badge) {
    return <div className='blend-value'>{getPowderQualityLabel(status)}</div>;
  }

  return (
    <>
      <div className='blend-value blend-value--quality'>
        <FontAwesomeIcon icon={badge.icon} style={{ color: badge.color }} />
        {getPowderQualityLabel(status)}
      </div>
      {badge.text && <div className='blend-value__sub'>{badge.text}</div>}
    </>
  );
};

/** One column of a comparison: the value itself, rendered for what it is. */
const BlendAttributeValue = ({ attributeKey, value, lotsByUri, uniqueLotUris }) => {
  switch (attributeKey) {
    case BLEND_ATTRIBUTE.MATERIAL_LOT:
      return (
        <MaterialLotValues
          lotUris={value || []}
          lotsByUri={lotsByUri}
          uniqueLotUris={uniqueLotUris}
        />
      );
    case BLEND_ATTRIBUTE.MATERIAL_TYPE:
      return <MaterialTypeValues materials={value || []} />;
    case BLEND_ATTRIBUTE.USAGE_CYCLES:
      return <UsageCyclesValue cycles={value ?? 0} />;
    case BLEND_ATTRIBUTE.POWDER_QUALITY:
      return <PowderQualityValue status={value} />;
    default:
      return null;
  }
};

const lotShape = PropTypes.objectOf(
  // eslint-disable-next-line camelcase
  PropTypes.shape({ name: PropTypes.string, material_name: PropTypes.string })
);

MaterialLotValues.propTypes = {
  lotUris: PropTypes.arrayOf(PropTypes.string).isRequired,
  lotsByUri: lotShape.isRequired,
  uniqueLotUris: PropTypes.instanceOf(Set).isRequired,
};

MaterialTypeValues.propTypes = {
  materials: PropTypes.arrayOf(PropTypes.shape({ uri: PropTypes.string, name: PropTypes.string }))
    .isRequired,
};

UsageCyclesValue.propTypes = { cycles: PropTypes.number.isRequired };

PowderQualityValue.propTypes = { status: PropTypes.string };
PowderQualityValue.defaultProps = { status: null };

BlendAttributeValue.propTypes = {
  attributeKey: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.number, PropTypes.string]),
  lotsByUri: lotShape,
  uniqueLotUris: PropTypes.instanceOf(Set),
};

BlendAttributeValue.defaultProps = {
  value: null,
  lotsByUri: {},
  uniqueLotUris: new Set(),
};

export default BlendAttributeValue;
